import React from 'react';
import { Routes, Route } from 'react-router-dom';
import Sidebar from '../components/Sidebar.tsx';
import PermissionRoute from '../components/PermissionRoute.tsx';
import DashboardHome from './DashboardHome.tsx';
import ItemKits from './ItemKits.tsx';
import AddItemKit from './AddItemKit.tsx';
import ViewItemKit from './ViewItemKit.tsx';
import EditItemKit from './EditItemKit.tsx';
import Items from './Items.tsx';
import EditItem from './EditItem.tsx';
import Users from './Users.tsx';
import EditUser from './EditUser.tsx';
import DeliveryAddresses from './DeliveryAddresses.tsx';
import AddDeliveryAddress from './AddDeliveryAddress.tsx';
import EditDeliveryAddress from './EditDeliveryAddress.tsx';
import Retailers from './Retailers.tsx';
import AddRetailer from './AddRetailer.tsx';
import EditRetailer from './EditRetailer.tsx';
import States from './States.tsx';
import AddState from './AddState.tsx';
import EditState from './EditState.tsx';
import Countries from './Countries.tsx';
import AddCountry from './AddCountry.tsx';
import EditCountry from './EditCountry.tsx';
import ReportedPosts from './ReportedPosts.tsx';
import Support from './Support.tsx';
import SearchAnalytics from './SearchAnalytics.tsx';
import PerformanceAnalytics from './PerformanceAnalytics.tsx';
import UserActivity from './UserActivity.tsx';
import Roles from './Roles.tsx';
import SuperUsers from './SuperUsers.tsx';
import MarketingMessages from './MarketingMessages.tsx';
import ProductClicks from './ProductClicks.tsx';
import CreatePost from './CreatePost.tsx';
import FuelStations from './FuelStations.tsx';
import FuelPriceReports from './FuelPriceReports.tsx';
import FuelAnalytics from './FuelAnalytics.tsx';
import CrashReports from './CrashReports.tsx';
import DeliverablePostcodes from './DeliverablePostcodes.tsx';
import Zones from './Zones.tsx';
import ZoneDetail from './ZoneDetail.tsx';
import Drivers from './Drivers.tsx';
import Orders from './Orders.tsx';
import OrderDetail from './OrderDetail.tsx';
import Coupons from './Coupons.tsx';
import PaymentGateways from './PaymentGateways.tsx';
import BulkNotifications from './BulkNotifications.tsx';
import AdsConfig from './AdsConfig.tsx';
import CacheConfig from './CacheConfig.tsx';
import FeedbackCampaigns from './FeedbackCampaigns.tsx';
import FeedbackCampaignEditor from './FeedbackCampaignEditor.tsx';
import FeedbackCampaignAnalytics from './FeedbackCampaignAnalytics.tsx';
import './Dashboard.css';

interface DashboardProps {
  isCollapsed: boolean;
  isMobileOpen: boolean;
  onMobileClose: () => void;
}

const Dashboard: React.FC<DashboardProps> = ({ isCollapsed, isMobileOpen, onMobileClose }) => {
  return (
    <div className="dashboard-layout">
      <Sidebar isCollapsed={isCollapsed} isMobileOpen={isMobileOpen} onMobileClose={onMobileClose} />
      <div className={`dashboard-content ${isCollapsed ? 'collapsed' : ''}`}>
        <Routes>
          <Route index element={<DashboardHome />} />
          {/* Catalog */}
          <Route path="item-kits" element={<PermissionRoute permission="catalog"><ItemKits /></PermissionRoute>} />
          <Route path="item-kits/new" element={<PermissionRoute permission="catalog"><AddItemKit /></PermissionRoute>} />
          <Route path="item-kits/view/:id" element={<PermissionRoute permission="catalog"><ViewItemKit /></PermissionRoute>} />
          <Route path="item-kits/edit/:id" element={<PermissionRoute permission="catalog"><EditItemKit /></PermissionRoute>} />
          <Route path="items" element={<PermissionRoute permission="catalog"><Items /></PermissionRoute>} />
          <Route path="items/edit/:id" element={<PermissionRoute permission="catalog"><EditItem /></PermissionRoute>} />
          {/* Management */}
          <Route path="users" element={<PermissionRoute permission="management"><Users /></PermissionRoute>} />
          <Route path="users/edit/:id" element={<PermissionRoute permission="management"><EditUser /></PermissionRoute>} />
          <Route path="delivery-addresses" element={<PermissionRoute permission="management"><DeliveryAddresses /></PermissionRoute>} />
          <Route path="delivery-addresses/new" element={<PermissionRoute permission="management"><AddDeliveryAddress /></PermissionRoute>} />
          <Route path="delivery-addresses/edit/:id" element={<PermissionRoute permission="management"><EditDeliveryAddress /></PermissionRoute>} />
          <Route path="retailers" element={<PermissionRoute permission="management"><Retailers /></PermissionRoute>} />
          <Route path="retailers/new" element={<PermissionRoute permission="management"><AddRetailer /></PermissionRoute>} />
          <Route path="retailers/edit/:id" element={<PermissionRoute permission="management"><EditRetailer /></PermissionRoute>} />
          <Route path="reported-posts" element={<PermissionRoute permission="management"><ReportedPosts /></PermissionRoute>} />
          <Route path="create-post" element={<PermissionRoute permission="management"><CreatePost /></PermissionRoute>} />
          <Route path="support" element={<PermissionRoute permission="management"><Support /></PermissionRoute>} />
          <Route path="marketing-messages" element={<PermissionRoute permission="management"><MarketingMessages /></PermissionRoute>} />
          <Route path="coupons" element={<PermissionRoute permission="management"><Coupons /></PermissionRoute>} />
          <Route path="bulk-notifications" element={<PermissionRoute permission="management"><BulkNotifications /></PermissionRoute>} />
          <Route path="ads-config" element={<PermissionRoute permission="management"><AdsConfig /></PermissionRoute>} />
          <Route path="cache-config" element={<PermissionRoute permission="management"><CacheConfig /></PermissionRoute>} />
          {/* Analytics */}
          <Route path="search-analytics" element={<PermissionRoute permission="analytics"><SearchAnalytics /></PermissionRoute>} />
          <Route path="performance-analytics" element={<PermissionRoute permission="analytics"><PerformanceAnalytics /></PermissionRoute>} />
          <Route path="product-clicks" element={<PermissionRoute permission="analytics"><ProductClicks /></PermissionRoute>} />
          <Route path="user-activity" element={<PermissionRoute permission="analytics"><UserActivity /></PermissionRoute>} />
          <Route path="crash-reports" element={<PermissionRoute permission="analytics"><CrashReports /></PermissionRoute>} />
          {/* Fuel */}
          <Route path="fuel-stations" element={<PermissionRoute permission="fuel"><FuelStations /></PermissionRoute>} />
          <Route path="fuel-reports" element={<PermissionRoute permission="fuel"><FuelPriceReports /></PermissionRoute>} />
          <Route path="fuel-analytics" element={<PermissionRoute permission="fuel"><FuelAnalytics /></PermissionRoute>} />
          {/* Geography */}
          <Route path="states" element={<PermissionRoute permission="geography"><States /></PermissionRoute>} />
          <Route path="states/new" element={<PermissionRoute permission="geography"><AddState /></PermissionRoute>} />
          <Route path="states/edit/:id" element={<PermissionRoute permission="geography"><EditState /></PermissionRoute>} />
          <Route path="countries" element={<PermissionRoute permission="geography"><Countries /></PermissionRoute>} />
          <Route path="countries/new" element={<PermissionRoute permission="geography"><AddCountry /></PermissionRoute>} />
          <Route path="countries/edit/:id" element={<PermissionRoute permission="geography"><EditCountry /></PermissionRoute>} />
          <Route path="deliverable-postcodes" element={<PermissionRoute permission="geography"><DeliverablePostcodes /></PermissionRoute>} />
          {/* Orders & Delivery */}
          <Route path="zones" element={<PermissionRoute permission="orders"><Zones /></PermissionRoute>} />
          <Route path="zones/:id" element={<PermissionRoute permission="orders"><ZoneDetail /></PermissionRoute>} />
          <Route path="drivers" element={<PermissionRoute permission="orders"><Drivers /></PermissionRoute>} />
          <Route path="orders" element={<PermissionRoute permission="orders"><Orders /></PermissionRoute>} />
          <Route path="orders/:id" element={<PermissionRoute permission="orders"><OrderDetail /></PermissionRoute>} />
          <Route path="payment-gateways" element={<PermissionRoute permission="orders"><PaymentGateways /></PermissionRoute>} />
          {/* Feedback */}
          <Route path="feedback-campaigns" element={<PermissionRoute permission="feedback_campaigns"><FeedbackCampaigns /></PermissionRoute>} />
          <Route path="feedback-campaigns/new" element={<PermissionRoute permission="feedback_campaigns"><FeedbackCampaignEditor /></PermissionRoute>} />
          <Route path="feedback-campaigns/edit/:id" element={<PermissionRoute permission="feedback_campaigns"><FeedbackCampaignEditor /></PermissionRoute>} />
          <Route path="feedback-campaigns/:id/analytics" element={<PermissionRoute permission="feedback_campaigns"><FeedbackCampaignAnalytics /></PermissionRoute>} />
          {/* System */}
          <Route path="roles" element={<PermissionRoute permission="superuser_mgmt"><Roles /></PermissionRoute>} />
          <Route path="superusers" element={<PermissionRoute permission="superuser_mgmt"><SuperUsers /></PermissionRoute>} />
        </Routes>
      </div>
    </div>
  );
};

export default Dashboard;
